import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, ArrowRight, Tag, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { articlesData } from '../data/articlesData';
import { logger } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const response = await fetch(`${API_URL}/api/articles`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            // Exclude "Vie à bord" articles — they have their own dedicated page
            const blogOnly = data.filter(
              (a) => (a.category || '').toUpperCase() !== 'VIE À BORD'
            );
            setArticles(blogOnly.map(article => ({
              ...article,
              image: article.image?.replace('w=1200', 'w=800') || article.image,
              readTime: article.read_time
            })));
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        logger.warn('API non disponible — fallback statique', err);
      }
      
      // Fallback to static data
      const staticArticles = Object.entries(articlesData)
        .filter(([, article]) => (article.category || '').toUpperCase() !== 'VIE À BORD')
        .map(([slug, article]) => ({
          slug,
          ...article,
          image: article.image.replace('w=1200', 'w=800'),
          readTime: article.readTime
        }))
        .sort((a, b) => new Date(b.publishedDate || b.published_date) - new Date(a.publishedDate || a.published_date));
      
      setArticles(staticArticles);
      setLoading(false);
    };

    fetchArticles();
  }, []);

  // Sort articles by date
  const sortedArticles = [...articles].sort((a, b) => 
    new Date(b.published_date || b.publishedDate) - new Date(a.published_date || a.publishedDate)
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1c1b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#d9fb06] animate-spin" />
      </div>
    );
  }

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-[#1a1c1b]">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-[#1a1c1b]">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <div className="text-center mb-12">
              <h1 className="font-black text-5xl md:text-7xl leading-tight text-[#d9fb06] uppercase mb-6">
                Blog & Conseils
              </h1>
              <p className="text-xl text-[#888680] max-w-3xl mx-auto">
                Guides, conseils et astuces d'expert pour réussir l'achat de votre bateau
              </p>
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-16 bg-[#302f2c]">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {sortedArticles.map((article, index) => {
                // "Nouveau" badge if published in the last 14 days
                const pubDate = new Date(
                  article.published_date || article.publishedDate || 0
                );
                const isNew =
                  !Number.isNaN(pubDate.getTime()) &&
                  Date.now() - pubDate.getTime() < 14 * 24 * 60 * 60 * 1000;
                return (
                <Card 
                  key={article.slug || index}
                  className="bg-[#1a1c1b] border border-[#3f4816] hover:border-[#d9fb06] transition-all duration-300 overflow-hidden group"
                >
                  {/* Image */}
                  <div className="h-64 overflow-hidden relative">
                    <img 
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {isNew && (
                      <span
                        className="absolute top-3 left-3 bg-[#d9fb06] text-[#1a1c1b] text-xs font-black uppercase px-2.5 py-1 rounded-full shadow-lg tracking-wider"
                        data-testid={`badge-new-${article.slug}`}
                      >
                        Nouveau
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Category & Date */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <Tag className="text-[#d9fb06]" size={16} />
                        <span className="text-[#d9fb06] text-sm font-semibold uppercase">{article.category}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-[#888680] text-sm">
                        <Calendar size={16} />
                        <span>{article.date}</span>
                      </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-bold text-[#d9fb06] mb-3 leading-tight">
                      {article.title}
                    </h2>

                    {/* Excerpt */}
                    <p className="text-[#888680] mb-4 leading-relaxed">
                      {article.excerpt}
                    </p>

                    {/* Read Time & CTA */}
                    <div className="flex items-center justify-between pt-4 border-t border-[#3f4816]">
                      <span className="text-[#888680] text-sm">{article.readTime || article.read_time} de lecture</span>
                      <Link to={`/blog/${article.slug}`}>
                        <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-6 py-2 font-semibold transition-all hover:scale-105 flex items-center space-x-2">
                          <span>Lire l'article</span>
                          <ArrowRight size={16} />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-[#1a1c1b]">
          <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
            <div className="bg-[#3f4816] rounded-lg p-12 text-center">
              <h2 className="text-3xl md:text-5xl font-black text-[#d9fb06] mb-6 uppercase">
                Besoin d'un expert pour votre projet ?
              </h2>
              <p className="text-[#dfddd6] text-xl mb-8 max-w-2xl mx-auto">
                Bénéficiez d'un accompagnement personnalisé pour trouver et acheter le bateau idéal
              </p>
              <Link to="/#contact">
                <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-10 py-6 text-lg font-bold uppercase transition-all hover:scale-105">
                  Réserver un appel gratuit
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Blog;
